"use client";

import { useAnimationSequence } from "@ui/hooks/useAnimationSequence";
import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { GridItem, MODEL_NAMES, TIMING, TRANSITIONS } from "../types";
import {
  PromptContentArea,
  ContentPlaceholder,
  ContentProcessingOverlay,
  PromptTextInput,
  TextPlaceholder,
  PromptControlBar,
  ControlItem,
  IconVideo,
  IconQuality,
  IconAspectRatio,
  SendButton,
  ContentVideo,
  ContentAudio,
  ModelSelector,
  IconLayers,
} from "../components";

const LIPSYNC_STATES = [
  "IDLE",
  "OPEN_MODEL_SELECTOR",
  "SELECT_MODEL",
  "CLOSE_MODEL_SELECTOR",
  "UPLOAD_VIDEO",
  "UPLOAD_AUDIO",
  "SENDING",
  "PROCESSING",
  "RESULT",
  "FINISHED",
  "RESET",
] as const;

export function LipsyncScene({
  onGenerationStarted,
  onComplete,
  autoStart = true,
}: {
  onGenerationStarted: (mediaUrl: string, mediaType: "image" | "video", label: string) => void;
  onComplete: (result: GridItem) => void;
  autoStart?: boolean;
}) {
  const t = useTranslations("ClientKeys.VideoImageGenerationHero");
  const { motion: m, start, store } = useAnimationSequence(LIPSYNC_STATES);

  // Auto-start animation if enabled
  useEffect(() => {
    if (!autoStart) return;

    const timer = setTimeout(() => {
      start();
    }, 500);
    return () => clearTimeout(timer);
  }, [start, autoStart]);

  useEffect(() => {
    const unsubscribe = store.subscribe(
      state => state.state,
      currentState => {
        const currentStateName = LIPSYNC_STATES[currentState];

        if (currentStateName === "PROCESSING") {
          onGenerationStarted(
            "https://eleven-public-cdn.elevenlabs.io/payloadcms/467cslq82ev-lipsync-result.mp4",
            "video",
            t("processing_lipsyncing"),
          );
        }

        if (currentStateName === "FINISHED") {
          onComplete({
            type: "lipsync",
            media: "https://eleven-public-cdn.elevenlabs.io/payloadcms/467cslq82ev-lipsync-result.mp4",
            status: "complete",
            hasAudio: true, // Lipsync video has audio
          });
        }
      },
    );
    return unsubscribe;
  }, [store, onGenerationStarted, onComplete, t]);

  return (
    <>
      <m.div className='tw-flex tw-gap-2'>
        <PromptContentArea sequencedMotion={m}>
          <ContentPlaceholder
            sequencedMotion={m}
            initial={{ opacity: 1 }}
            animate={{
              UPLOAD_VIDEO: { opacity: 0 },
              FINISHED: { opacity: 1 },
            }}
            transition={{
              UPLOAD_VIDEO: TRANSITIONS.fast,
              FINISHED: TRANSITIONS.instant,
            }}
          />

          <ContentVideo
            sequencedMotion={m}
            src='https://eleven-public-cdn.elevenlabs.io/payloadcms/467cslq82ev-lipsync-result.mp4'
            render={["UPLOAD_VIDEO"]}
            animatePresence={{ initial: false }}
            initial={{ opacity: 0 }}
            animate={{
              UPLOAD_VIDEO: { opacity: 1 },
              FINISHED: { opacity: 0 },
            }}
            exit={{ opacity: 0, transition: TRANSITIONS.slow }}
            transition={{
              UPLOAD_VIDEO: { ...TRANSITIONS.slow, delay: TIMING.DELAY.SHORT },
              FINISHED: TRANSITIONS.instant,
            }}
          />
          <ContentProcessingOverlay
            sequencedMotion={m}
            render={["PROCESSING"]}
            initial={{ opacity: 0 }}
            animate={{
              PROCESSING: { opacity: 1 },
              RESULT: { opacity: 0 },
            }}
            transition={{
              PROCESSING: { ...TRANSITIONS.medium, delay: TIMING.DELAY.LONG },
              RESULT: TRANSITIONS.medium,
            }}
            className='tw-absolute tw-inset-0 tw-z-10'
            spinner={
              <m.div
                animate={{
                  PROCESSING: { rotate: 360 },
                }}
                transition={{
                  PROCESSING: TRANSITIONS.spinner,
                }}
                className='tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full'
              />
            }
          />
        </PromptContentArea>

        <PromptContentArea sequencedMotion={m} render={["CLOSE_MODEL_SELECTOR"]}>
          <ContentPlaceholder
            sequencedMotion={m}
            initial={{ opacity: 1 }}
            animate={{
              UPLOAD_AUDIO: { opacity: 0 },
              FINISHED: { opacity: 1 },
            }}
            transition={{
              UPLOAD_AUDIO: TRANSITIONS.medium,
              FINISHED: TRANSITIONS.instant,
            }}
          />

          <ContentAudio
            sequencedMotion={m}
            render={["UPLOAD_AUDIO"]}
            animatePresence={{ initial: false }}
            initial={{ opacity: 0 }}
            animate={{
              UPLOAD_AUDIO: { opacity: 1 },
              FINISHED: { opacity: 0 },
            }}
            exit={{ opacity: 0, transition: TRANSITIONS.slow }}
            transition={{
              UPLOAD_AUDIO: { ...TRANSITIONS.slow, delay: TIMING.DELAY.MEDIUM },
              FINISHED: TRANSITIONS.instant,
            }}
          />
          <ContentProcessingOverlay
            sequencedMotion={m}
            render={["PROCESSING"]}
            initial={{ opacity: 0 }}
            animate={{
              PROCESSING: { opacity: 1 },
              RESULT: { opacity: 0 },
            }}
            transition={{
              PROCESSING: { ...TRANSITIONS.medium, delay: TIMING.DELAY.LONG },
              RESULT: TRANSITIONS.medium,
            }}
            className='tw-absolute tw-inset-0 tw-z-10'
            spinner={
              <m.div
                animate={{
                  PROCESSING: { rotate: 360 },
                }}
                transition={{
                  PROCESSING: TRANSITIONS.spinner,
                }}
                className='tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full'
              />
            }
          />
        </PromptContentArea>
      </m.div>

      <PromptTextInput
        sequencedMotion={m}
        style={{ overflow: "hidden" }}
        animate={{
          IDLE: { height: "auto", opacity: 1 },
          CLOSE_MODEL_SELECTOR: { height: 0, opacity: 0 },
        }}
        transition={{
          IDLE: TRANSITIONS.fast,
          CLOSE_MODEL_SELECTOR: { ...TRANSITIONS.medium, delay: TIMING.DELAY.SHORT },
        }}
      >
        <TextPlaceholder
          sequencedMotion={m}
          text={t("prompt_placeholder")}
          render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}
          animatePresence={{ initial: false }}
          initial={{ opacity: 1, scale: 1 }}
          animate={{
            IDLE: { opacity: 1, scale: 1 },
          }}
          exit={{ opacity: 0, scale: 0.95, transition: TRANSITIONS.medium }}
        />
      </PromptTextInput>

      <PromptControlBar
        sequencedMotion={m}
        controls={
          <>
            <ControlItem
              icon={<IconVideo className='tw-size-5' />}
              sequencedMotion={m}
              label={
                <div className='tw-relative tw-h-full tw-flex tw-items-center'>
                  <m.span className='f-paragraph-03' render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}>
                    {MODEL_NAMES.WAN}
                  </m.span>
                  <m.span className='f-paragraph-03' render={["CLOSE_MODEL_SELECTOR"]}>
                    {MODEL_NAMES.LIPSYNC}
                  </m.span>

                  <ModelSelector
                    sequencedMotion={m}
                    currentModel={MODEL_NAMES.WAN}
                    targetModel={MODEL_NAMES.LIPSYNC}
                    render={["OPEN_MODEL_SELECTOR", "SELECT_MODEL", "-CLOSE_MODEL_SELECTOR"]}
                    animatePresence={{ initial: false }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{
                      OPEN_MODEL_SELECTOR: { opacity: 1, y: 0 },
                      SELECT_MODEL: { opacity: 1, y: 0 },
                    }}
                    exit={{ opacity: 0, y: -10, transition: TRANSITIONS.fast }}
                    transition={{
                      OPEN_MODEL_SELECTOR: { ...TRANSITIONS.medium, delay: TIMING.DELAY.SHORT },
                      SELECT_MODEL: TRANSITIONS.slow,
                    }}
                  />
                </div>
              }
            />

            <ControlItem
              icon={<IconQuality className='tw-size-5' />}
              label='720p'
              sequencedMotion={m}
              className='tw-hidden md:tw-flex'
              render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}
            />

            <ControlItem
              icon={<IconAspectRatio className='tw-size-5' />}
              label='16:9'
              sequencedMotion={m}
              render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}
              className='tw-hidden md:tw-flex'
            />

            <ControlItem
              icon={<IconLayers className='tw-size-5' />}
              label='1'
              sequencedMotion={m}
              render={["CLOSE_MODEL_SELECTOR"]}
            />
          </>
        }
        button={
          <SendButton
            sequencedMotion={m}
            animate={{
              SENDING: {
                scale: [1, 0.92, 1],
                backgroundColor: ["#F5F3F1", "#dbdad9", "#F5F3F1"], // Darker when pressed
              },
            }}
            transition={{
              SENDING: TRANSITIONS.slow,
            }}
          />
        }
      />
    </>
  );
}
